// src/components/base/Modal.jsx
import React, { useContext, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContextProvider";
import AvatarCropper from "./AvatarCropper";
import { MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody } from "mdb-react-ui-kit";
import { acquireOwnedScrollLock } from "../../utils/ownedScrollLock";

/** Kis, imperatív toast — nincs React state, nincs ütközés a modal animációival */
function showImperativeToast(message, { duration = 4000, type = "warning" } = {}) {
  // Egyetlen konténer a toasthoz
  let container = document.getElementById("global-toast-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "global-toast-container";
    Object.assign(container.style, {
      position: "fixed",
      top: "16px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: "999999",
      display: "grid",
      gap: "8px",
      width: "min(560px, 92vw)",
      pointerEvents: "none",
    });
    document.body.appendChild(container);
  }

  // Maga a toast elem
  const el = document.createElement("div");
  const isWarn = type === "warning";
  const bg = isWarn ? "rgba(220,38,38,0.12)" : "rgba(17,24,39,0.85)";
  const bd = isWarn ? "1px solid rgba(220,38,38,0.35)" : "1px solid rgba(255,255,255,0.15)";
  const color = isWarn ? "rgb(220,38,38)" : "#fff";

  Object.assign(el.style, {
    pointerEvents: "auto",
    backdropFilter: "blur(6px)",
    background: bg,
    border: bd,
    color,
    borderRadius: "10px",
    padding: "10px 12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    fontSize: "14px",
    lineHeight: "1.4",
    opacity: "0",
    transition: "opacity 160ms ease",
    whiteSpace: "pre-line",
  });
  el.textContent = message;

  container.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = "1";
  });

  const t = setTimeout(() => {
    el.style.opacity = "0";
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      if (container.childElementCount === 0 && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }, 180);
  }, duration);

  el.addEventListener("click", () => {
    clearTimeout(t);
    el.style.opacity = "0";
    setTimeout(() => el.parentNode && el.parentNode.removeChild(el), 180);
  });
}

export default function Modal(props) {
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const releaseScrollLockRef = useRef(null);

  // Share the owner-aware lock with invite/contact modals. In particular, a
  // forced global unlock here used to re-enable scrolling underneath another
  // nested modal or after route navigation.
  useEffect(() => {
    if (!props.visible) return undefined;
    releaseScrollLockRef.current = acquireOwnedScrollLock(document.body);
    return () => {
      releaseScrollLockRef.current?.();
      releaseScrollLockRef.current = null;
    };
  }, [props.visible]);

  const handleDone = async (fileOrBlob) => {
    try {
      if (props.modalParam?.savePhotoAsFile) {
        await props.modalParam.savePhotoAsFile(fileOrBlob);
      }
    } finally {
      context.closeModal();
    }
  };

  // Reakció-biztos értesítés: közvetlen DOM toast
  const notifyUnsupported = useCallback((msg) => {
    requestAnimationFrame(() => {
      setTimeout(() => showImperativeToast(msg || t('settings.avatarCropper.unsupportedFile')), 0);
    });
  }, []);

  return (
    <MDBModal staticBackdrop tabIndex="-1" closeOnEsc={false} show={props.visible} className="crop-modal">
      <MDBModalDialog scrollable centered>
        <MDBModalContent>
          <MDBModalHeader>
            <div className="title-text">{t('settings.avatarCropper.editProfilePicture')}</div>
            <button
              type="button"
              className="button outline medium"
              onClick={() => props.hideModal()}
              style={{ minWidth: "unset" }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M9.06 8l2.47-2.47a.75.75 0 1 0-1.06-1.06L8 6.94 5.53 4.47A.75.75 0 0 0 4.47 5.53L6.94 8l-2.47 2.47a.75.75 0 1 0 1.06 1.06L8 9.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L9.06 8Z"
                  fill="#fff"
                />
              </svg>
            </button>
          </MDBModalHeader>

          <MDBModalBody>
            <h2>{t('settings.avatarCropper.setProfilePicture')}</h2>
            <AvatarCropper onDone={handleDone} handleClose={props.hideModal} onUnsupported={notifyUnsupported} />
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
